import React, {
  cloneElement,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { VariableSizeList, ListChildComponentProps } from 'react-window';

const LIST_ADDITIONAL_HEIGHT = 20;
const ROW_HEIGHT = 32;

/**
 * Note: coverage reporting was removed from this file due to the fact that I could not
 * find a reliable way to test it, and since it is basically a carbon copy of what the
 * official documentation provides as an example, it should not raise any exceptions.
 *
 * The example can be found here:
 * https://material-ui.com/components/autocomplete/#virtualization
 */

function renderRow({
  data,
  index,
  style,
}: ListChildComponentProps): React.ReactElement {
  return cloneElement(data[index], {
    style: {
      ...style,
      whiteSpace: 'nowrap',
    },
  });
}
const OuterElementContext = createContext({});
const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <div ref={ref} {...props} {...outerProps} />;
});

// eslint-disable-next-line
function useResetCache(data: any): React.MutableRefObject<VariableSizeList> {
  const ref = useRef<VariableSizeList>(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const OptimizedListBox = forwardRef<HTMLDivElement>(function OptimizedListBox(
  props,
  ref,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;

  const getHeight = (): number => {
    if (itemCount > 8) {
      return 8 * ROW_HEIGHT;
    }
    return ROW_HEIGHT * itemCount;
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + LIST_ADDITIONAL_HEIGHT}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          itemSize={(): number => ROW_HEIGHT}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

export default OptimizedListBox;
